import { Helmet } from "react-helmet-async";

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AppFillip",
  alternateName: "AppFillip Digital Media",
  url: "https://appfillip.com",
  logo: "https://appfillip.com/logo.png",
  description:
    "Global App Store Optimization and App Marketing Agency. Expert ASO services, app development, user acquisition and AI brand citation for apps worldwide.",
  foundingDate: "2022",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    availableLanguage: "English",
  },
  sameAs: [
    "https://linkedin.com/company/appfillip",
    "https://twitter.com/appfillip",
    "https://instagram.com/appfillip",
    "https://youtube.com/@appfillip",
    "https://facebook.com/appfillip",
  ],
  areaServed: ["US", "GB", "AU", "AE", "CA", "DE", "JP", "SG", "IN"],
  knowsAbout: [
    "App Store Optimization",
    "Mobile App Marketing",
    "ASO",
    "User Acquisition",
    "App Development",
    "Google Play Store Optimization",
    "iOS App Marketing",
  ],
};

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "App Store Optimization Services",
  provider: { "@type": "Organization", name: "AppFillip" },
  serviceType: "App Store Optimization",
  description:
    "Professional ASO services including keyword research, listing optimization, screenshot design, A/B testing, and ratings management for iOS and Android apps.",
  areaServed: "Worldwide",
  url: "https://appfillip.com/aso",
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is App Store Optimization (ASO)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "App Store Optimization (ASO) is the process of improving an app's visibility and conversion rate in app stores like the Apple App Store and Google Play Store through keyword optimization, listing improvements, screenshot design, and ratings management.",
      },
    },
    {
      "@type": "Question",
      name: "How long does ASO take to show results?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "ASO typically shows initial ranking improvements within 4–8 weeks. Significant organic download growth is usually visible within 3–6 months of consistent optimization.",
      },
    },
    {
      "@type": "Question",
      name: "Does AppFillip work with apps in all categories?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. AppFillip provides specialized ASO for 50+ app categories including gaming, dating, health, fintech, edtech, ecommerce, food delivery, travel, social networking, music, meditation, productivity, and more.",
      },
    },
    {
      "@type": "Question",
      name: "Does AppFillip offer app development services?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. AppFillip offers full app development services including iOS, Android, Flutter, and React Native — all built with ASO in mind from day one.",
      },
    },
  ],
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://appfillip.com",
    },
  ],
};

export default function SeoHead() {
  return (
    <Helmet>
      <html lang="en" />
      <title>
        AppFillip — Global App Store Optimization & App Marketing Agency | ASO Services
      </title>
      <meta
        name="description"
        content="AppFillip is the global App Store Optimization (ASO) and app marketing agency. We help apps rank #1 on Apple App Store & Google Play Store across 15+ markets. Expert ASO services, app development, user acquisition & AI brand citation. Serving USA, UK, Australia, UAE & more. Get a free ASO audit today."
      />
      <meta
        name="keywords"
        content="app store optimization, global ASO agency, app marketing agency, mobile app marketing, app store optimization services, ASO services, app ranking agency, iOS app marketing, Android app optimization, Google Play Store optimization, app store keyword research, user acquisition mobile apps, best ASO agency, app store optimization company, mobile app growth agency, app store listing optimization, ASO consultant, app marketing services, global app marketing"
      />
      <link rel="canonical" href="https://appfillip.com" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />

      {/* Open Graph */}
      <meta
        property="og:title"
        content="AppFillip — Global App Store Optimization & App Marketing Agency"
      />
      <meta
        property="og:description"
        content="Rank your app #1 on App Store & Google Play. Expert ASO, App Development & User Acquisition. Free ASO Audit Available."
      />
      <meta property="og:url" content="https://appfillip.com" />
      <meta property="og:site_name" content="AppFillip" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content="https://appfillip.com/og/default.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="AppFillip — Global App Store Optimization & App Marketing Agency"
      />
      <meta
        name="twitter:description"
        content="Rank your app #1 on App Store & Google Play. Expert ASO services. Free audit available."
      />
      <meta
        name="twitter:image"
        content="https://appfillip.com/og/default.png"
      />

      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>
      <script type="application/ld+json">
        {JSON.stringify(SERVICE_SCHEMA)}
      </script>
      <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      <script type="application/ld+json">
        {JSON.stringify(BREADCRUMB_SCHEMA)}
      </script>
    </Helmet>
  );
}
